import { Badge } from '@ledgerloom/ui';
import { Link } from 'react-router-dom';
import { Time, badgeTone } from '../components/ui';
import { human } from '../format';

// The traffic layer on the Agent Desk (docs/design/desk-traffic.md): the exchanges between
// agents in a window, read from GET /v1/desk/traffic, summed here into marks on each tile,
// chips on the counterparts of the pressed tile, a band of columns under the map, and the
// Exchanges tab in the panel. Patterns: data-display/timeline for the band, forms/filter-chips
// for the window, data-display/list-view for the list. The window lives in the URL.

export interface Actor { kind: 'agent' | 'user' | 'group'; id: string; name: string | null }
export interface Exchange {
  id: string; kind: 'signal' | 'handoff' | 'consult' | 'ruling'; at: string; what: string; status: string;
  from: Actor; to: Actor | null; heard: Actor[]; turns: number | null; verdict: string | null; severity: string | null;
  thread_id: string | null; correlation_id: string | null;
}
export interface Traffic { read_to: string; items: Exchange[] }

export const PRESETS: Array<[string, string]> = [['today', 'Today'], ['7d', '7 days'], ['30d', '30 days']];
const DAY = 86_400_000;
const midnight = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

// The window from the URL: a preset, or from and to as times. Today ends at the coming midnight
// so the path stays the same across refreshes and the live reload keeps its place.
export function windowOf(params: URLSearchParams): { from: Date; to: Date; preset: string | null } {
  const from = params.get('from'); const to = params.get('to');
  if (from && to && !Number.isNaN(Date.parse(from)) && !Number.isNaN(Date.parse(to))) return { from: new Date(from), to: new Date(to), preset: null };
  const preset = params.get('window') ?? 'today';
  const end = new Date(midnight(new Date()).getTime() + DAY);
  const days = preset === '7d' ? 7 : preset === '30d' ? 30 : 1;
  return { from: new Date(end.getTime() - days * DAY), to: end, preset: PRESETS.some(([p]) => p === preset) ? preset : 'today' };
}

export const actorName = (a: Actor) => a.name ?? (a.kind === 'user' ? 'Someone' : a.kind === 'group' ? 'A group' : 'An agent no longer hired');

// Who sent and who received each exchange, by agent id. A handoff to a group reaches each of its
// members, and a signal reaches every agent that heard it.
function sides(x: Exchange, groups: Map<string, string[]>): { sent: string[]; got: string[] } {
  const agentsOf = (a: Actor | null) => (!a ? [] : a.kind === 'agent' ? [a.id] : a.kind === 'group' ? groups.get(a.id) ?? [] : []);
  return { sent: agentsOf(x.from), got: x.kind === 'signal' ? x.heard.map((h) => h.id) : agentsOf(x.to) };
}

export const KIND_WORD: Record<Exchange['kind'], [string, string, string]> = {
  signal: ['Raised', 'Heard', 'signal'], handoff: ['Handed', 'Was handed', 'handoff'], consult: ['Asked', 'Was asked', 'consult'], ruling: ['Ruled', 'Got a ruling', 'ruling'],
};
const KINDS = Object.keys(KIND_WORD) as Exchange['kind'][];
const plural = (n: number, one: string) => `${n} ${one}${n === 1 ? '' : 's'}`;

export interface Marks { sent: Record<Exchange['kind'], number>; got: Record<Exchange['kind'], number> }
const zero = () => Object.fromEntries(KINDS.map((k) => [k, 0])) as Record<Exchange['kind'], number>;

// Each agent's marks: what it sent and what it received, by kind.
export function marksByAgent(items: Exchange[], groups: Map<string, string[]>): Map<string, Marks> {
  const m = new Map<string, Marks>();
  const at = (id: string) => { let r = m.get(id); if (!r) { r = { sent: zero(), got: zero() }; m.set(id, r); } return r; };
  for (const x of items) {
    const { sent, got } = sides(x, groups);
    for (const id of sent) at(id).sent[x.kind] += 1;
    for (const id of got) at(id).got[x.kind] += 1;
  }
  return m;
}

// The exchanges one agent took part in, and for each other agent what passed between them,
// said from that other agent's side ("Heard 1 signal", "Was handed 2").
export function counterparts(agent: string, items: Exchange[], groups: Map<string, string[]>): { mine: Exchange[]; chips: Map<string, string> } {
  const mine: Exchange[] = [];
  const counts = new Map<string, Record<string, number>>();
  for (const x of items) {
    const { sent, got } = sides(x, groups);
    const outward = sent.includes(agent); const inward = got.includes(agent);
    if (!outward && !inward) continue;
    mine.push(x);
    for (const other of outward ? got : sent) {
      if (other === agent) continue;
      const c = counts.get(other) ?? {}; const key = `${x.kind}|${outward ? 1 : 0}`;
      c[key] = (c[key] ?? 0) + 1; counts.set(other, c);
    }
  }
  const chips = new Map([...counts].map(([id, c]) => [id, Object.entries(c).map(([key, n]) => {
    const [kind, out] = key.split('|') as [Exchange['kind'], string];
    const [did, took] = KIND_WORD[kind];
    return `${out === '1' ? took : did} ${n}`;
  }).join(' · ')]));
  return { mine, chips };
}

// The marks on a tile: a shape per kind, filled when sent and outlined when received, with the count.
export function TileMarks({ m }: { m: Marks | undefined }) {
  if (!m) return null;
  const parts: Array<{ k: Exchange['kind']; dir: 'sent' | 'got'; n: number; text: string }> = [];
  for (const k of KINDS) {
    if (m.sent[k]) parts.push({ k, dir: 'sent', n: m.sent[k], text: `${KIND_WORD[k][0]} ${plural(m.sent[k], KIND_WORD[k][2])}` });
    if (m.got[k]) parts.push({ k, dir: 'got', n: m.got[k], text: `${KIND_WORD[k][1]}: ${plural(m.got[k], KIND_WORD[k][2])}` });
  }
  if (!parts.length) return null;
  return (
    <span className="desk-marks">
      {parts.map((p) => <span key={`${p.k}${p.dir}`} className={`desk-mark mark-${p.k} ${p.dir}`}><i aria-hidden="true" />{p.n}<span className="sr-only">{p.text}</span></span>)}
    </span>
  );
}

// The band: the window in columns of an hour, six hours, or a day, each column its exchanges by
// kind. A column with exchanges is a button that narrows the window to it.
export function Band({ items, from, to, onNarrow }: { items: Exchange[]; from: Date; to: Date; onNarrow: (from: Date, to: Date) => void }) {
  const span = to.getTime() - from.getTime();
  const step = span <= 2 * DAY ? 3_600_000 : span <= 14 * DAY ? 6 * 3_600_000 : DAY;
  const n = Math.ceil(span / step);
  const cols = Array.from({ length: n }, () => zero());
  for (const x of items) {
    const i = Math.floor((Date.parse(x.at) - from.getTime()) / step);
    if (cols[i]) cols[i]![x.kind] += 1;
  }
  const max = Math.max(1, ...cols.map((c) => KINDS.reduce((s, k) => s + c[k], 0)));
  const fmt = (d: Date) => (step >= DAY ? d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric' }));
  return (
    <ol className="desk-band" aria-label="Exchanges over the period">
      {cols.map((c, i) => {
        const start = new Date(from.getTime() + i * step); const end = new Date(Math.min(to.getTime(), start.getTime() + step));
        const total = KINDS.reduce((s, k) => s + c[k], 0);
        const words = KINDS.filter((k) => c[k]).map((k) => plural(c[k], KIND_WORD[k][2])).join(', ');
        const bars = KINDS.filter((k) => c[k]).map((k) => <i key={k} className={`mark-${k}`} style={{ flexGrow: c[k] }} aria-hidden="true" />);
        return (
          <li key={i} style={{ ['--h' as string]: `${(total / max) * 100}%` }}>
            {total ? (
              <button type="button" className="desk-col" onClick={() => onNarrow(start, end)} aria-label={`${fmt(start)} to ${fmt(end)}: ${words}`}>
                <span className="desk-col-n" aria-hidden="true">{total}</span><span className="desk-col-bar">{bars}</span>
              </button>
            ) : <span className="desk-col empty" aria-hidden="true" />}
          </li>
        );
      })}
    </ol>
  );
}

// One agent's exchanges in the window, newest first, each with its counterpart and the way into
// the thread, the signal, and the whole chain.
export function Exchanges({ agent, items }: { agent: string; items: Exchange[] }) {
  if (!items.length) return <p className="faint small">No exchanges with other agents in this period.</p>;
  return (
    <ul className="desk-rows" aria-label="Exchanges">
      {items.map((x) => {
        const outward = x.from.kind === 'agent' && x.from.id === agent;
        const other = x.kind === 'signal' ? (outward ? (x.heard.length ? `heard by ${x.heard.map(actorName).join(', ')}` : 'heard by no agent') : `raised by ${actorName(x.from)}`)
          : outward ? `to ${x.to ? actorName(x.to) : 'nobody'}` : `from ${actorName(x.from)}`;
        // A domain is an id people need as it is; a signal type, work class, or verdict reads as words.
        const what = x.kind === 'ruling' ? human(x.verdict ?? '') : x.what.includes(':') ? <code>{x.what}</code> : human(x.what);
        const head = <>{outward ? KIND_WORD[x.kind][0] : KIND_WORD[x.kind][1]} {what}</>;
        const open = x.kind === 'signal' ? '/agents/signals' : `/agents/consults/${x.thread_id ?? x.id}`;
        return (
          <li key={`${x.kind}:${x.id}`} className="desk-row">
            <span className="item-title">{head}<Badge tone={badgeTone(x.kind)} className="mark-">{human(x.status)}</Badge></span>
            <span className="item-sub">{other}{x.turns && x.turns > 1 ? ` · ${plural(x.turns, 'turn')}` : ''} · <Time iso={x.at} /></span>
            <span className="row small">
              <Link to={open}>{x.kind === 'signal' ? 'Open the signal' : 'Open the thread'}</Link>
              {x.correlation_id && <Link to={`/agents/traces/${x.correlation_id}`}>Open the whole chain</Link>}
            </span>
          </li>
        );
      })}
    </ul>
  );
}
